import { useState, useCallback, useMemo } from 'react';
import {
  TARGET_SYSTEM_AUTH_COMPATIBILITY,
  AUTH_METHOD_FIELDS,
  DEFAULT_CUSTOM_SCHEDULE,
  SENSITIVE_FIELDS_BY_AUTH_METHOD
} from '../constants';
import {
  initializeFormData,
  isConnectionField,
  validateForm,
  getScenarioFieldDefaults,
  convertCustomScheduleToObject,
  convertLocalHourToUTC
} from '../utils';
import { transformFormToPayload } from '../utils/transformFormToPayload';
import * as agentScannerService from '~/services/agentScannerService';

/**
 * Checks if a field is sensitive (requires special handling in edit mode)
 * @param {string} field - Field name
 * @param {string} authMethod - Authentication method
 * @returns {boolean} True if field is sensitive
 */
const isSensitiveField = (field, authMethod) => {
  const sensitiveFields = SENSITIVE_FIELDS_BY_AUTH_METHOD[authMethod] || [];

  return sensitiveFields.includes(field);
};

/**
 * Removes old auth-specific fields from state
 */
const removeAuthFields = (state, authMethod) => {
  const fieldsToRemove = AUTH_METHOD_FIELDS[authMethod] || [];
  const cleanedState = { ...state };

  fieldsToRemove.forEach((field) => {
    delete cleanedState[field];
  });

  return cleanedState;
};

/**
 * Handles target system change and returns new state
 */
const handleTargetSystemChange = (prevState, newTargetSystem) => {
  const compatibleAuthMethods =
    TARGET_SYSTEM_AUTH_COMPATIBILITY[newTargetSystem] || [];
  const isCurrentAuthCompatible = compatibleAuthMethods.includes(
    prevState.authMethod
  );
  const newAuthMethod = isCurrentAuthCompatible ? prevState.authMethod : '';

  // Clean up old fields
  const cleanedState = removeAuthFields(prevState, prevState.authMethod);

  // Get scenario-specific field defaults
  const newAuthFields = getScenarioFieldDefaults(
    newTargetSystem,
    newAuthMethod
  );

  return {
    ...cleanedState,
    targetSystem: newTargetSystem,
    authMethod: newAuthMethod,
    ...newAuthFields
  };
};

/**
 * Handles auth method change and returns new state
 */
const handleAuthMethodChange = (prevState, newAuthMethod) => {
  const cleanedState = removeAuthFields(prevState, prevState.authMethod);

  // Get scenario-specific field defaults
  const newAuthFields = getScenarioFieldDefaults(
    prevState.targetSystem,
    newAuthMethod
  );

  return {
    ...cleanedState,
    authMethod: newAuthMethod,
    ...newAuthFields
  };
};

/**
 * Blurs the run-frequency select element after modal closes
 */
const blurRunFrequencySelect = () => {
  setTimeout(() => {
    const selectElement = document.getElementById('run-frequency');

    if (selectElement) {
      selectElement.blur();
    }
  }, 0);
};

/**
 * Processes custom schedule field change
 */
const processCustomScheduleChange = (prevSchedule, field, value) => {
  const newSchedule = {
    ...prevSchedule,
    [field]: value
  };

  // Reset fields when switching to months
  if (field === 'intervalUnit' && value === 'months') {
    newSchedule.intervalNumber = 1;
    newSchedule.repeatDays = [];
  }

  // Clear repeat days when switching away from weeks
  const isSwitchingFromWeeks =
    field === 'intervalUnit' &&
    value !== 'weeks' &&
    prevSchedule.intervalUnit === 'weeks';

  if (isSwitchingFromWeeks) {
    newSchedule.repeatDays = [];
  }

  return newSchedule;
};

/**
 * Helper to get sensitive field value for API submission
 * In edit mode: returns null if field wasn't modified (preserves existing secret)
 * In edit mode: returns actual value if field was modified (updates secret)
 * In add mode: always returns actual value
 */
const getSensitiveFieldValue = (
  fieldName,
  fieldValue,
  authMethod,
  mode,
  modifiedSensitiveFields
) => {
  if (mode === 'edit' && isSensitiveField(fieldName, authMethod)) {
    if (!modifiedSensitiveFields.has(fieldName)) {
      return null; // Preserve existing value
    }
  }

  return fieldValue ?? null;
};

/**
 * Build auth parameters object with sensitive field handling
 */
const buildAuthParameters = (formData, mode, modifiedSensitiveFields) => {
  const getSensitiveValue = (field, value) =>
    getSensitiveFieldValue(
      field,
      value,
      formData.authMethod,
      mode,
      modifiedSensitiveFields
    );

  return {
    target: formData.targetSystem,
    method: formData.authMethod,
    accessKeyId: formData.accessKeyId,
    secretAccessKey: getSensitiveValue(
      'secretAccessKey',
      formData.secretAccessKey
    ),
    awsRegion: formData.awsRegion,
    projectId: formData.projectId,
    serviceAccountEmail: formData.serviceAccountEmail,
    privateKey: getSensitiveValue('privateKey', formData.privateKey),
    location: formData.location,
    clientId: formData.clientId,
    clientSecret: getSensitiveValue('clientSecret', formData.clientSecret),
    tenantId: formData.tenantId,
    scope: formData.scope,
    // GoDaddy ANS fields
    gdKey: formData.gdKey,
    gdSecret: getSensitiveValue('gdSecret', formData.gdSecret),
    environment: formData.environment
  };
};

/**
 * Build run policy schedule object from form data.
 * All times sent to the backend are in UTC.
 */
const buildRunPolicySchedule = (formData) => {
  const utcHour = convertLocalHourToUTC(formData.at);

  const schedule = {
    at: utcHour,
    syncReview: formData.syncReview
  };

  // Handle structured runFrequency object
  if (formData.runFrequency && typeof formData.runFrequency === 'object') {
    const { unit, interval, on } = formData.runFrequency;

    schedule.unit = unit || 'day';
    schedule.interval = String(interval || 1);

    if (on !== undefined) {
      schedule.on = on;
    }
  } else {
    // Fallback for string values (backward compatibility)
    schedule.unit = 'day';
    schedule.interval = '1';
  }

  // Add optional advanced settings
  if (formData.timeout) {
    schedule.timeout = formData.timeout;
  }
  if (formData.maxRetryAttempts) {
    schedule.maxRetryAttempts = formData.maxRetryAttempts;
  }

  return schedule;
};

/**
 * Build complete form payload for API submission
 */
const buildFormPayload = (formData, mode, modifiedSensitiveFields) => ({
  name: formData.name,
  description: formData.description,
  parameters: buildAuthParameters(formData, mode, modifiedSensitiveFields),
  runPolicy: buildRunPolicySchedule(formData),
  emailNotificationsEnabled: formData.emailNotificationsEnabled,
  recipientEmails: formData.recipientEmails
});

/**
 * Custom hook for managing scanner configuration form state and handlers
 *
 * @param {Object} initialData - Initial form data from props
 * @param {string} mode - Form mode ('add' or 'edit')
 * @param {Function} onSubmit - Submit callback
 * @param {Object} context - Additional context for API payload
 * @param {string} context.organizationId - Organization ID
 * @param {string} context.ownerId - Owner ID
 * @param {string} context.businessGroup - Business Group ID
 * @param {string} context.targetSystemId - Target System UUID
 * @returns {Object} Form state and handlers
 */
const useScannerForm = (initialData, mode, onSubmit, context = {}) => {
  // Form data state
  const [formData, setFormData] = useState(() =>
    initializeFormData(initialData, mode)
  );

  // Track which sensitive fields have been modified in edit mode
  // This is used to determine which fields should be sent as null (unchanged) vs actual value (modified)
  const [modifiedSensitiveFields, setModifiedSensitiveFields] = useState(
    new Set()
  );

  // UI state
  const [connectionStatus, setConnectionStatus] = useState('idle');
  const [connectionError, setConnectionError] = useState(null);
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [customSchedule, setCustomSchedule] = useState(DEFAULT_CUSTOM_SCHEDULE);

  // Scheduled scan status state
  // Initializes from state field for edit mode, or SCHEDULED for add mode
  const [scheduleStatus, setScheduleStatus] = useState(() => {
    if (mode === 'edit' && initialData?.state) {
      return initialData.state;
    }

    return 'SCHEDULED';
  });

  // Reset connection status when connection fields change
  const resetConnectionStatusIfNeeded = useCallback(
    (field) => {
      setFormData((prev) => {
        const shouldReset =
          isConnectionField(field, prev.authMethod) &&
          connectionStatus !== 'idle';

        if (shouldReset) {
          setConnectionStatus('idle');
          setConnectionError(null);
        }

        return prev;
      });
    },
    [connectionStatus]
  );

  // Generic change handler for all form fields
  const handleChange = useCallback(
    (field, value) => {
      // Handle special field types with early returns
      if (field === 'targetSystem') {
        setFormData((prev) => handleTargetSystemChange(prev, value));
        resetConnectionStatusIfNeeded(field);

        return;
      }

      if (field === 'authMethod') {
        setFormData((prev) => handleAuthMethodChange(prev, value));
        resetConnectionStatusIfNeeded(field);
        setModifiedSensitiveFields(new Set());

        return;
      }

      if (field === 'runFrequency' && value === 'Custom') {
        setFormData((prev) => ({ ...prev, [field]: value }));
        setShowCustomModal(true);
        resetConnectionStatusIfNeeded(field);

        return;
      }

      // Track modifications to sensitive fields in edit mode
      if (mode === 'edit' && isSensitiveField(field, formData.authMethod)) {
        setModifiedSensitiveFields((prev) => new Set(prev).add(field));
      }

      // Handle all other fields
      setFormData((prev) => ({ ...prev, [field]: value }));
      resetConnectionStatusIfNeeded(field);
    },
    [resetConnectionStatusIfNeeded, mode, formData.authMethod]
  );

  // Handle custom schedule modal save
  const handleCustomScheduleSave = useCallback(() => {
    const structuredSchedule = convertCustomScheduleToObject(customSchedule);

    setFormData((prev) => ({ ...prev, runFrequency: structuredSchedule }));
    setShowCustomModal(false);
    blurRunFrequencySelect();
  }, [customSchedule]);

  // Handle custom schedule modal cancel
  const handleCustomScheduleCancel = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      runFrequency: { unit: 'day', interval: 1 }
    }));
    setShowCustomModal(false);
    blurRunFrequencySelect();
  }, []);

  // Handle custom schedule field changes
  const handleCustomScheduleChange = useCallback((field, value) => {
    setCustomSchedule((prev) =>
      processCustomScheduleChange(prev, field, value)
    );
  }, []);

  // Toggle advanced settings accordion
  const toggleAdvancedSettings = useCallback(() => {
    setShowAdvancedSettings((prev) => !prev);
  }, []);

  // Toggle email notifications
  const handleEmailNotificationsToggle = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      emailNotificationsEnabled: !prev.emailNotificationsEnabled
    }));
  }, []);

  // Handle scheduled scan status toggle
  const handleScheduleStatusToggle = useCallback(() => {
    setScheduleStatus((prevStatus) => {
      // Toggle logic:
      // SCHEDULED → PAUSED
      // PAUSED → SCHEDULED
      // STOPPED → SCHEDULED (allows user to fix configuration)
      if (prevStatus === 'SCHEDULED') {
        return 'PAUSED';
      }

      return 'SCHEDULED';
    });
  }, []);

  // Add email to recipient list
  const handleAddEmail = useCallback((email) => {
    setFormData((prev) => ({
      ...prev,
      recipientEmails: [...prev.recipientEmails, email]
    }));
  }, []);

  // Remove email from recipient list
  const handleRemoveEmail = useCallback((emailToRemove) => {
    setFormData((prev) => ({
      ...prev,
      recipientEmails: prev.recipientEmails.filter(
        (email) => email !== emailToRemove
      )
    }));
  }, []);

  // Handle connection test
  // Returns true if connection test succeeds, false otherwise
  const handleTestConnection = useCallback(async () => {
    setConnectionStatus('checking');
    setConnectionError(null);

    try {
      // Reuse buildAuthParameters for consistency (use 'add' mode to get actual values, not nulls)
      const formPayloadData = {
        name: formData.name || 'Test Connection',
        description: '',
        parameters: buildAuthParameters(formData, 'add', new Set()),
        runPolicy: {
          runFrequency: 'Daily',
          runTime: '2',
          syncReview: 'auto-resolve'
        }
      };

      // Transform to API payload to get connection structure
      const apiPayload = transformFormToPayload(formPayloadData, context);

      // Extract connection parameters for test
      const { connection } = apiPayload;

      // Parse authParameters from string to JSON object
      const authParams = JSON.parse(connection.authParameters);

      // Get connection ID (null if not defined)
      const connectionId =
        formData.connectionId || initialData?.connection?.id || null;

      // Get sensitive fields for current auth method
      const sensitiveFields =
        SENSITIVE_FIELDS_BY_AUTH_METHOD[formData.authMethod] || [];

      // Set sensitive fields to null if not provided by user
      // This applies to both add and edit modes
      const processedAuthParams = { ...authParams };

      sensitiveFields.forEach((sensitiveField) => {
        // Map form field names to API field names
        const fieldMapping = {
          secretAccessKey: 'keySecret',
          clientSecret: 'clientSecret',
          privateKey: 'privateKey'
        };
        const apiFieldName = fieldMapping[sensitiveField] || sensitiveField;

        // If the field exists in authParams and is empty/not set, set it to null
        if (
          apiFieldName in processedAuthParams &&
          !processedAuthParams[apiFieldName]
        ) {
          processedAuthParams[apiFieldName] = null;
        }
      });

      // Add connection ID to the parameters
      const connectionParams = {
        ...processedAuthParams,
        id: connectionId
      };

      // Call test connection service
      const result = await agentScannerService.testConnection({
        targetSystemType: connection.type,
        connectionParams,
        context
      });

      if (result) {
        setConnectionStatus('success');

        return true;
      }
      setConnectionStatus('error');
      setConnectionError(
        'Connection test failed. Please verify your credentials.'
      );

      return false;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Connection test error:', error);
      setConnectionStatus('error');
      setConnectionError(
        error?.response?.data?.message ||
          error?.message ||
          'Unable to test connection. Please try again.'
      );

      return false;
    }
  }, [formData, context, initialData]);

  // Validate connection before submission (test connection only if needed)
  const validateConnection = useCallback(async () => {
    const shouldTestConnection = connectionStatus !== 'success';

    if (!shouldTestConnection) {
      // No need to test connection
      return true;
    }

    // Perform test connection
    const testResult = await handleTestConnection();

    if (!testResult) {
      // Update error message for connection test failure
      setConnectionError(
        'Connection test failed. Please verify your credentials before submitting.'
      );

      return false;
    }

    // Test connection succeeded
    return true;
  }, [formData, mode, connectionStatus, handleTestConnection]);

  // Form submission handler
  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();

      // Validate connection (similar to required fields validation)
      validateConnection().then((connectionValid) => {
        if (!connectionValid) {
          // Connection validation failed - don't submit
          return;
        }

        // Build form payload data
        const formPayloadData = buildFormPayload(
          formData,
          mode,
          modifiedSensitiveFields
        );

        // Transform to API payload format
        const apiPayload = transformFormToPayload(formPayloadData, {
          ownerId: context.ownerId || '',
          businessGroup: context.businessGroup || '',
          orgId: context.organizationId || '',
          connectionId:
            mode === 'edit'
              ? formData.connectionId || initialData?.connection?.id
              : undefined
        });

        // Add state to the payload (top level)
        apiPayload.state = scheduleStatus;

        // For edit mode, include the ID
        if (mode === 'edit' && (formData.id || initialData?.id)) {
          apiPayload.id = formData.id || initialData?.id;
        }

        if (onSubmit) {
          onSubmit(apiPayload);
        }
      });
    },
    [
      formData,
      mode,
      initialData,
      onSubmit,
      context,
      modifiedSensitiveFields,
      validateConnection,
      scheduleStatus
    ]
  );

  // Form validation
  // Only validates required fields - connection test happens automatically on submit
  // Also checks schedule status - form is invalid if status is STOPPED
  const isFormValid = useMemo(() => {
    const basicValidation = validateForm(formData, mode);

    // Disable form submission if schedule status is STOPPED
    // This forces user to fix the configuration before submitting
    if (scheduleStatus === 'STOPPED') {
      return false;
    }

    // Button is enabled when all required fields are completed
    // Connection validation happens automatically in handleSubmit via validateConnection
    return basicValidation;
  }, [formData, mode, scheduleStatus]);

  return {
    // State
    formData,
    connectionStatus,
    connectionError,
    showCustomModal,
    showAdvancedSettings,
    customSchedule,
    isFormValid,
    scheduleStatus,

    // Handlers
    handleChange,
    handleSubmit,
    handleTestConnection,
    handleCustomScheduleSave,
    handleCustomScheduleCancel,
    handleCustomScheduleChange,
    toggleAdvancedSettings,
    handleEmailNotificationsToggle,
    handleAddEmail,
    handleRemoveEmail,
    handleScheduleStatusToggle
  };
};

export default useScannerForm;
